import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import {
  assertSegmentIstLückenlosGefüllt,
  createElement,
  createStreckeWithMauerUndBreite,
  resetArrangement,
} from "./utils";
import {
  getAngrenzendeTeile,
  getEntferntAngrenzendeFreiraumBreiteSumme,
  getSollZwischenabstandVerbose,
  getZwischenabstandAnSeite,
  getZwischenabstandAnSeiteVerbose,
  LINKS,
  nullEpsilon,
  RECHTS,
} from "../arrangement/elementFunctions";
import { Arrangement } from "../arrangement/Arrangement";
import { Segment } from "../arrangement/Segment";
import { ZElement } from "../arrangement/ZElement";
import { findPathIntersectionCached } from "oc-app/metadata/GraphMetadata";
import { v2 } from "oc-threejs/short3";
import * as Maths from "oc-commons/Maths";
import { MObjekt } from "../../metamodell";
import { PAuswahlwert } from "../../metamodell_paths";

export default {
  name: "TestElementeAufrücken",
};

const getPartReihenfolge = (s: Segment) =>
  s.getParts().map((part) => {
    if (part.isEmpty) {
      return "Leerraum";
    }
    if ((part as ZElement).isElement) {
      return (part as ZElement).form.Key;
    }
    return (part.constructor as any).CLASS_NAME;
  });

const assertKeineAufeinanderZeigendenSnaps = (s: Segment) => {
  const parts = s.getParts().filter((p) => !p.isEmpty);
  for (let i = 1; i < parts.length; i++) {
    const a = parts[i - 1];
    const b = parts[i];
    assert.isFalse(
      a.snap === +1 && b.snap === -1,
      `Ungültige Snap-Kollision zwischen Parts ${i - 1} und ${i}: +1/-1`
    );
  }
};

const removeEmptyByRank = async (s: Segment, rank: "left" | "middle" | "right") => {
  const empties = [...s.empties].sort((a, b) => s.getX(a, 0) - s.getX(b, 0));
  assert.isTrue(empties.length >= 3, "Es werden mindestens 3 Leerräume für den Test erwartet");
  const byRank = {
    left: empties[0],
    middle: empties[Math.floor(empties.length / 2)],
    right: empties[empties.length - 1],
  } as const;
  APP.arrangement.setSelectedObjects(byRank[rank]);
  await APP.arrangement.removeSelectedObjects();
};

const json = {
  inspiration: "Inspiration SYSTEM WPC PLATINUM XL Taupe-Grau",
  zaunart: "Sichtschutz",
  serie: "SYSTEM",
  grundriss: {
    ecken: [
      {
        id: 3,
        punkt: {
          x: -5,
          y: 0,
        },
      },
      {
        id: 4,
        punkt: {
          x: 5,
          y: 0,
        },
      },
    ],
    kanten: [
      {
        ecken: [3, 4],
      },
    ],
  },
  segmente: [
    {
      breite: 10,
      partsAndPfosten: [
        {
          type: "Pfosten",
          x0: 0.7475,
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "Elementhalter Weiß",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metalltorpfosten",
          pfostenartAuswahlwertIndividualisiert: false,
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          pfostenkappeAuswahlwert: "Pfostenkappe_inklusiv",
          ankerInnen: false,
          ausgleichGefälle: false,
          pfostenAuswahlwert: "Torpfosten Einbetonieren Anthrazit",
        },
        {
          type: "Element",
          breite: 3.61,
          höhe: 1.8,
          snap: 1,
          isBreiteKonfigurierbar: true,
          x0: 0.8425,
          füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Taupe-Grau",
          füllungAuswahlwertIndividualisiert: false,
          gehflügelAuswahlwert: "Gehflügel_links",
          drückerAuswahlwert: "Drücker_Knauf_edelstahl",
          profilzylinderAuswahlwert: "Profilzylinder_mit",
          rahmenAuswahlwert: "Tor_Rahmen_Anthrazit",
          öffnungAuswahlwert: "Öffnung_nach_innen",
          anschlagAuswahlwert: "Anschlag_links",
          schieberichtungAuswahlwert: "Schieberichtung_links",
          schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
          füllungsseiteAuswahlwert: "Füllungsseite_B",
          anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
          anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
          abschlussleisteAuswahlwertIndividualisiert: false,
          profilKategorieAuswahlwert: "Dekorprofile_Mass_Tore_30cm",
          profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
          profilleisteAuswahlwert: "Dekor_Adapter_anthrazit",
          eAntriebAuswahlwert: "E_Antrieb_ohne",
          zubehörAnzahl: {
            Handsender: 0,
            "Handsender Design": 0,
            Schlüsselschalter: 0,
            "Funk-Codeschloss": 0,
            "Bluetooth Schalter": 0,
            "Connect Gateway": 0,
            Lichtschranke: 0,
            "Techniker Key": 0,
          },
          toröffnerAuswahlwert: "Toröffner_ohne",
          form: "Doppeltor",
          massAuswahlwert: "Doppeltor_Easy",
          profilPosition: 4,
          showProfil: null,
          anschlussAufRechts: false,
          gehflügelbreite: 1.8,
          standflügelbreite: 1.8,
          torIstFertig: false,
          elementNr: 0,
          isStandardMass: false,
          detailSerie: "SYSTEM WPC PLATINUM XL",
          anMauer: "AnMauer_nein",
          isMasstor: true,
          isMasselement: false,
          isMassProfil: false,
          isBestellseite: false,
          isEAntrieb: false,
          key: "2036 SYSTEM WPC PLATINUM XL Mass Doppeltor einseitig_30",
          lichteWeite: 3.64,
          einsteckschlossAuswahlwert: "Einsteckschloss_inklusive",
          beschlagsatzAuswahlwert: "Beschlagsatz_inklusive",
          hideRahmen: false,
          showProfilausrichtung: true,
          showAdapterleiste: true,
          hasAnschlagprofil: false,
          bestelllisteArtikel: ["3689", "2036", "2625_anthrazit", "1251", "2550"],
          artikelID: "2036",
          füllungName: "Füllung",
          ausrichtungCode: "3lilb",
          pdf: {},
          elementID: 0,
        },
        {
          type: "Pfosten",
          x0: 4.4675,
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "Elementhalter Weiß",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metalltorpfosten",
          pfostenartAuswahlwertIndividualisiert: false,
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          pfostenkappeAuswahlwert: "Pfostenkappe_inklusiv",
          ankerInnen: false,
          ausgleichGefälle: false,
          pfostenAuswahlwert: "Torpfosten Einbetonieren Anthrazit",
        },
        {
          type: "Element",
          breite: 0.98,
          höhe: 1.8,
          snap: 0,
          isBreiteKonfigurierbar: true,
          x0: 4.56,
          füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Taupe-Grau",
          füllungAuswahlwertIndividualisiert: false,
          gehflügelAuswahlwert: "Gehflügel_links",
          drückerAuswahlwert: "Drücker_Knauf_edelstahl",
          profilzylinderAuswahlwert: "Profilzylinder_mit",
          rahmenAuswahlwert: "Tor_Rahmen_Anthrazit",
          öffnungAuswahlwert: "Öffnung_nach_innen",
          anschlagAuswahlwert: "Anschlag_links",
          schieberichtungAuswahlwert: "Schieberichtung_links",
          schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
          füllungsseiteAuswahlwert: "Füllungsseite_B",
          anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
          anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
          abschlussleisteAuswahlwertIndividualisiert: false,
          profilKategorieAuswahlwert: "Dekorprofile_Sondertore_WPC_PLATINUM_XL_30cm",
          profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
          zubehörAnzahl: {
            Handsender: 0,
            "Handsender Design": 0,
            Schlüsselschalter: 0,
            "Funk-Codeschloss": 0,
            "Bluetooth Schalter": 0,
            "Connect Gateway": 0,
            Lichtschranke: 0,
            "Techniker Key": 0,
          },
          toröffnerAuswahlwert: "Toröffner_ohne",
          torschließerAuswahlwert: "Torschließer_ohne",
          form: "Einzeltor",
          massAuswahlwert: "Einzeltor_Easy",
          profilPosition: 4,
          showProfil: null,
          anschlussAufRechts: false,
          gehflügelbreite: 0,
          standflügelbreite: 0,
          torIstFertig: false,
          elementNr: 1,
          isStandardMass: true,
          detailSerie: "SYSTEM WPC PLATINUM XL",
          anMauer: "AnMauer_nein",
          isMasstor: false,
          isMasselement: false,
          isMassProfil: false,
          isBestellseite: false,
          isEAntrieb: false,
          elementID: 0,
        },
        {
          type: "Pfosten",
          x0: 5.5525,
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "Elementhalter Weiß",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metalltorpfosten",
          pfostenartAuswahlwertIndividualisiert: false,
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          pfostenkappeAuswahlwert: "Pfostenkappe_inklusiv",
          ankerInnen: false,
          ausgleichGefälle: false,
          pfostenAuswahlwert: "Torpfosten Einbetonieren Anthrazit",
        },
        {
          type: "Pfosten",
          x0: 7.4325,
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "Elementhalter Weiß",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metalltorpfosten",
          pfostenartAuswahlwertIndividualisiert: false,
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          pfostenkappeAuswahlwert: "Pfostenkappe_inklusiv",
          ankerInnen: false,
          ausgleichGefälle: false,
          pfostenAuswahlwert: "Torpfosten Einbetonieren Anthrazit",
        },
        {
          type: "Element",
          breite: 0.98,
          höhe: 1.8,
          snap: 0,
          isBreiteKonfigurierbar: true,
          x0: 7.525,
          füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Taupe-Grau",
          füllungAuswahlwertIndividualisiert: false,
          gehflügelAuswahlwert: "Gehflügel_links",
          drückerAuswahlwert: "Drücker_Knauf_edelstahl",
          profilzylinderAuswahlwert: "Profilzylinder_mit",
          rahmenAuswahlwert: "Tor_Rahmen_Anthrazit",
          öffnungAuswahlwert: "Öffnung_nach_innen",
          anschlagAuswahlwert: "Anschlag_links",
          schieberichtungAuswahlwert: "Schieberichtung_links",
          schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
          füllungsseiteAuswahlwert: "Füllungsseite_B",
          anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
          anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
          abschlussleisteAuswahlwertIndividualisiert: false,
          profilKategorieAuswahlwert: "Dekorprofile_Sondertore_WPC_PLATINUM_XL_30cm",
          profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
          zubehörAnzahl: {
            Handsender: 0,
            "Handsender Design": 0,
            Schlüsselschalter: 0,
            "Funk-Codeschloss": 0,
            "Bluetooth Schalter": 0,
            "Connect Gateway": 0,
            Lichtschranke: 0,
            "Techniker Key": 0,
          },
          toröffnerAuswahlwert: "Toröffner_ohne",
          torschließerAuswahlwert: "Torschließer_ohne",
          form: "Einzeltor",
          massAuswahlwert: "Einzeltor_Easy",
          profilPosition: 4,
          showProfil: null,
          anschlussAufRechts: false,
          gehflügelbreite: 0,
          standflügelbreite: 0,
          torIstFertig: false,
          elementNr: 2,
          isStandardMass: true,
          detailSerie: "SYSTEM WPC PLATINUM XL",
          anMauer: "AnMauer_nein",
          isMasstor: false,
          isMasselement: false,
          isMassProfil: false,
          isBestellseite: false,
          isEAntrieb: false,
          elementID: 0,
        },
        {
          type: "Pfosten",
          x0: 8.5175,
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "Elementhalter Weiß",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metalltorpfosten",
          pfostenartAuswahlwertIndividualisiert: false,
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          pfostenkappeAuswahlwert: "Pfostenkappe_inklusiv",
          ankerInnen: false,
          ausgleichGefälle: false,
          pfostenAuswahlwert: "Torpfosten Einbetonieren Anthrazit",
        },
      ],
      verteilungsstrategieAuswahlwert: "Individuell",
    },
  ],
  ecken: [
    {
      position: {
        x: -5,
        y: 0,
        z: 0,
      },
    },
    {
      position: {
        x: 5,
        y: 0,
        z: 0,
      },
    },
  ],
  aufbauten: [],
  lichtleistenSplitter: 0,
  lichtleistenZubehör: {
    "Edelstahl-Blende_Lichtleiste_Steckzaun": 0,
    "3834 SYSTEM Licht Smart Control Set": 1,
    "3836 SYSTEM Licht Fernbedienung": 0,
    "3835 SYSTEM Licht Bewegungsmelder": 0,
    "3837 SYSTEM Licht Steuerungsadapter": 0,
    "3838 SYSTEM Licht Kabel Zusatz-Steuerungsadapter": 0,
  },
  pfostenartAuswahlwert: "Metalltorpfosten",
  pfostenfarbeAuswahlwert: "Anthrazit",
  bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
  elementhalterAuswahlwert: "Elementhalter Weiß",
  verteilungsstrategieAuswahlwert: "StandardLinks",
  bestellliste: [
    {
      ArtikelID: "3689",
      Anzahl: 1,
      AnzahlEditiert: false,
      ElementIndizes: [1, 2],
    },
    {
      ArtikelID: "3689",
      Anzahl: 1,
      AnzahlEditiert: false,
      ElementIndizes: [1, 2],
    },
    {
      ArtikelID: "2036",
      Anzahl: 1,
      AnzahlEditiert: false,
      Mass: "Dreifachtor 360 x 180 cm + ",
      ElementIndizes: [0],
    },
    {
      ArtikelID: "2625_anthrazit",
      Anzahl: 1,
      AnzahlEditiert: false,
      ElementIndizes: [0],
    },
    {
      ArtikelID: "1251",
      Anzahl: 2,
      AnzahlEditiert: false,
      ElementIndizes: [0, 2],
    },
    {
      ArtikelID: "2550",
      Anzahl: 2,
      AnzahlEditiert: false,
      ElementIndizes: [0, 2],
    },
    {
      ArtikelID: "2059",
      Anzahl: 1,
      AnzahlEditiert: false,
      ElementIndizes: [2],
    },
    {
      ArtikelID: "1251",
      Anzahl: 1,
      AnzahlEditiert: false,
      ElementIndizes: [0, 2],
    },
    {
      ArtikelID: "2550",
      Anzahl: 1,
      AnzahlEditiert: false,
      ElementIndizes: [0, 2],
    },
  ],
  customAnzahlen: {},
  gesamtpreis: 5200.55,
  baugruppen: [
    [
      {
        ArtikelID: "2036",
        Anzahl: 1,
        MassOrBauseitsTag: "Dreifachtor 360 x 180 cm + ",
      },
      {
        ArtikelID: "3689",
        Anzahl: 1,
        MassOrBauseitsTag: "Dreifachtor 360 x 180 cm + ",
      },
      {
        ArtikelID: "2625_anthrazit",
        Anzahl: 1,
        MassOrBauseitsTag: "Dreifachtor 360 x 180 cm + ",
      },
      {
        ArtikelID: "1251",
        Anzahl: 2,
        MassOrBauseitsTag: "Dreifachtor 360 x 180 cm + ",
      },
      {
        ArtikelID: "2550",
        Anzahl: 2,
        MassOrBauseitsTag: "Dreifachtor 360 x 180 cm + ",
      },
    ],
    [
      {
        ArtikelID: "3689",
        Anzahl: 1,
        MassOrBauseitsTag: "",
      },
      {
        ArtikelID: "2059",
        Anzahl: 1,
        MassOrBauseitsTag: "",
      },
      {
        ArtikelID: "1251",
        Anzahl: 1,
        MassOrBauseitsTag: "",
      },
      {
        ArtikelID: "2550",
        Anzahl: 1,
        MassOrBauseitsTag: "",
      },
    ],
  ],
  warnungen: [],
  montageanleitungenArtikelIDs: ["2036"],
  inspirationsBilder: ["/Auswahlwert/system-wpc-platinum-xl-taupe-grau-52.jpg"],
  pageName: "Zauneditor",
  userDefinedArticles: [],
  hinweistexte: ["WORKFLOW_Dreifachtor"],
};

export const testSnapNachLeerraumLinks: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.fromJSON(json);
  agt.update3d();

  const s = agt.segmente[0];
  await removeEmptyByRank(s, "left");

  assert.deepEquals(
    ["Doppeltor", "Einzeltor", "Leerraum", "Einzeltor", "Leerraum"],
    getPartReihenfolge(s),
    "Nach Entfernen des linken Leerraums ist die erwartete Reihenfolge falsch"
  );
  assertKeineAufeinanderZeigendenSnaps(s);
};

export const testSnapNachLeerraumRechts: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.fromJSON(json);
  agt.update3d();

  const s = agt.segmente[0];
  await removeEmptyByRank(s, "right");

  assert.deepEquals(
    ["Leerraum", "Doppeltor", "Einzeltor", "Leerraum", "Einzeltor"],
    getPartReihenfolge(s),
    "Nach Entfernen des rechten Leerraums ist die erwartete Reihenfolge falsch"
  );
  assertKeineAufeinanderZeigendenSnaps(s);
};

export const testSnapNachLeerraumMitte: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.fromJSON(json);
  agt.update3d();

  const s = agt.segmente[0];
  await removeEmptyByRank(s, "middle");

  assert.deepEquals(
    ["Leerraum", "Doppeltor", "Einzeltor", "Einzeltor"],
    getPartReihenfolge(s),
    "Nach Entfernen des mittleren Leerraums ist die erwartete Reihenfolge falsch"
  );
  assertKeineAufeinanderZeigendenSnaps(s);
};
